import { useEffect, useState } from "react";

import styled from "styled-components/macro";

import { AddPaymentMethodWrapper } from "Payments/AddPaymentMethod";
import { usePaymentPage } from "Payments/PaymentPage/PaymentPageContext";
import { motion } from "framer-motion";

import { usePaymentForm } from "../../PaymentFormContext";
import { useInitialPaymentType } from "../../paymentFormStateHooks";

import PaymentMethodElements from "./PaymentMethodElements";

export default function NewPaymentMethod() {
  const {
    dispatch,
    state: {
      user: { ID: userID, name, billingAddress, sharableWith, phoneNumber },
      userInitial: { loginLevel: loginLevelInitial },
      network: { isACHAllowed },
      paymentMethods,
      selectedPaymentMethodID,
      authCodeData,
      tabID,
      network,
    },
  } = usePaymentPage();
  const {
    state: { hasAmountURLParam },
    dispatch: formDispatch,
  } = usePaymentForm();

  const { initialPaymentType, setInitialPaymentType } = useInitialPaymentType();

  useEffect(
    function checkIfShouldPushToMethodSelected() {
      if (selectedPaymentMethodID) {
        formDispatch({
          type: "setFormStage",
          stage: "paymentInfo",
          value: "methodSelected",
        });
      }
    },
    [selectedPaymentMethodID, formDispatch]
  );

  // if the user comes to the page and is logged in and amount is pre-filled in, the credit card form shows right away and should not animate in
  const isInitialPaymentMethodStep =
    hasAmountURLParam && loginLevelInitial === "full" && !isACHAllowed;

  // show the close button if any other payment method types are allowed (for now, just ACH)
  const showCloseButton = isACHAllowed;

  const handleCloseClick = () => {
    formDispatch({
      type: "setFormStage",
      stage: "paymentInfo",
      value: "chooseAddMethodType",
    });

    setTimeout(() => {
      // delaying this change prevents the card background from changing during the exit animation
      setInitialPaymentType("");
    }, 300);
  };

  const handleSuccess = ({ paymentMethods, newPaymentMethodID, ...rest }) => {
    dispatch({
      type: "setSelectedPaymentMethodID",
      payload: newPaymentMethodID,
    });
    dispatch({
      type: "setPaymentMethods",
      payload: paymentMethods,
    });
  };

  const [inputErrors, setInputErrors] = useState([]);
  const [shouldClearDisplayMessages, setShouldClearDisplayMessages] =
    useState(false);

  const authProps = {
    tabID,
  };

  if (authCodeData?.loginValue && !userID) {
    authProps.canSaveCard = false;
    authProps[authCodeData.loginValueType] = authCodeData.loginValue;
  }

  const [showCard, setShowCard] = useState(isInitialPaymentMethodStep);

  useEffect(() => {
    if (initialPaymentType === "bank") {
      setShowCard(false);
    } else {
      setShowCard(true);
    }
  }, [initialPaymentType]);

  return (
    <Wrapper
      initial={!isInitialPaymentMethodStep && { height: 0, opacity: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{
        opacity: 0,
        height: 0,
        scale: 0.5,
        transition: { type: "tween" },
      }}
    >
      <AddPaymentMethodWrapper
        onSuccess={handleSuccess}
        mustSaveCard={false}
        isAlreadyExisting={false}
        userID={userID}
        existingPaymentMethod={false}
        hasMobilePhone={phoneNumber && true}
        sharableWith={sharableWith}
        hasOtherPaymentMethods={paymentMethods?.length > 0}
        setInputErrors={setInputErrors}
        {...authProps}
      >
        <PaymentMethodElements
          showCloseButton={showCloseButton}
          isInitialPaymentMethodStep={isInitialPaymentMethodStep}
          handleCloseClick={handleCloseClick}
          showCard={showCard}
          setInputErrors={setInputErrors}
          shouldClearDisplayMessages={shouldClearDisplayMessages}
          setShouldClearDisplayMessages={setShouldClearDisplayMessages}
          billingAddress={billingAddress}
          name={name}
          inputErrors={inputErrors}
          phoneNumber={phoneNumber}
          sharableWith={sharableWith}
          network={network}
        />
      </AddPaymentMethodWrapper>
    </Wrapper>
  );
}

const Wrapper = styled(motion.div)`
  flex-grow: 1;
  height: auto;
`;
